import { ReactNode, useEffect, useState } from "react";
import {
    confirmUserAttribute,
    fetchAuthSession,
    fetchUserAttributes,
    sendUserAttributeVerificationCode,
} from "aws-amplify/auth";
import { useDispatch } from "react-redux";
import { Logomark } from "@/common/Logo/Logo.tsx";
import { setSelectedWorkspace } from "@/features/Workspace/Workspace.slice.ts";
import { useUserVerification } from "@/app/Authentication.slice.ts";
import { useAuth } from "@/app/auth/AuthContext.tsx";
import { useAuthSurface } from "@/app/auth/authSurface.ts";
import AuthFlow from "@/app/auth/AuthFlow.tsx";
import { auditTokenOf } from "@/app/audit/auditProspect.ts";
import AuditSignedInFlow from "@/app/audit/AuditSignedInFlow.tsx";
import { forgetSignInWithoutPasskey } from "@/app/auth/passkeyNudge.ts";
import { useStartSessionMutation } from "@/services/apis/api.ts";
import { isApiError } from "@/services/apis/apiError";
import { deleteCookie } from "@/util/cookie.ts";
import { GA_SESSION_ID_COOKIE } from "@/app/auth/gaSession.ts";
import Loading from "@/common/Loading";
import Form from "@/common/Form";
import { FormError, FormStack } from "@/common/FormLayout";
import Button from "@/common/Button";
import * as yup from "yup";
import Panel from "@/common/Panel/Panel.tsx";
import Link from "@/common/Link";
import { clearCognitoLocalStorage, useSignOut } from "@/hooks/use-signout.ts";
import { continueWithSingleSignOn } from "@/app/auth/ssoDiscovery.ts";

export default function CognitoAuthenticator({
    children,
}: {
    children: ReactNode;
}) {
    const { authStatus, isResolvingRedirect } = useAuth();
    const { serverSession } = useAuthSurface();
    const [needsVerification, setNeedsVerification] = useUserVerification();
    const dispatch = useDispatch();

    useEffect(() => {
        if (needsVerification || authStatus !== "authenticated") {
            return;
        }
        Promise.all([fetchUserAttributes(), fetchAuthSession()]).then(
            ([attributes, session]) => {
                const idToken = session.tokens?.idToken;
                const identities = idToken?.payload?.identities;

                if (identities?.[0 as keyof typeof identities]) {
                    // This is a social login
                    return;
                }

                if (attributes.email_verified === "false") {
                    setNeedsVerification(true);
                }
            },
        );
    }, [needsVerification, setNeedsVerification, authStatus]);

    useEffect(() => {
        if (authStatus === "unauthenticated") {
            dispatch(setSelectedWorkspace(undefined));
            // However the last session ended, the next sign-in starts clean.
            forgetSignInWithoutPasskey();
        }
    }, [authStatus, dispatch]);

    // An audit link is a page of its own, also for somebody signed in: the
    // app has no screen at that path. Inside the session all the same, so a
    // sign-up that ends up here is still provisioned.
    const signedIn =
        auditTokenOf(window.location.pathname) === null ? (
            children
        ) : (
            <AuditSignedInFlow />
        );

    let content: ReactNode;
    if (authStatus === "configuring" || isResolvingRedirect) {
        // Restoring the session, or resolving the Google OAuth redirect.
        content = <Loading />;
    } else if (authStatus === "unauthenticated") {
        content = <AuthFlow />;
    } else if (needsVerification) {
        // Authenticated, but a changed email still needs verifying.
        content = <EmailCodeVerification />;
    } else if (serverSession) {
        // Mounted afresh on every arrival here -- a sign-in, a restored
        // session, a verified email change -- and each mount starts a session.
        content = <ServerSession>{signedIn}</ServerSession>;
    } else {
        content = signedIn;
    }

    return <main className="h-full">{content}</main>;
}

/**
 * Starts a management-api session before rendering anything that calls it.
 * Every other request only finds an existing account (CognitoAuthenticator on
 * the Api side), so this is where a first sign-in becomes one, and where a
 * changed name, picture or verified email reaches it.
 */
const ServerSession = ({ children }: { children: ReactNode }) => {
    const [startSession, { isError, error }] = useStartSessionMutation();
    // Latched here rather than read back from the mutation. Switching
    // workspace resets the whole Api cache (store.ts), which wipes the
    // mutation's own isSuccess with it -- and nothing below re-runs the
    // effect, so the spinner would come back over a signed-in app and stay
    // there. Having started a session is a fact about this sign-in, not
    // cached server data.
    const [started, setStarted] = useState(false);
    const [missingToken, setMissingToken] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;

        fetchAuthSession()
            .then(({ tokens }) => {
                if (cancelled) {
                    return;
                }

                const idToken = tokens?.idToken?.toString();
                if (!idToken) {
                    setMissingToken(true);
                    return;
                }

                startSession({ idToken })
                    .unwrap()
                    .then(() => {
                        // The account exists now, so the sign-up attribution
                        // the request carried has done its job (baseQuery.ts)
                        deleteCookie("potentialSignUp");
                        deleteCookie("auditProspect");
                        deleteCookie(GA_SESSION_ID_COOKIE);
                        if (!cancelled) {
                            setStarted(true);
                        }
                    })
                    // A failure is rendered from the mutation's own state
                    .catch(() => undefined);
            })
            .catch(() => {
                if (!cancelled) {
                    setMissingToken(true);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [attempt, startSession]);

    if (started) {
        return children;
    }

    if (isError || missingToken) {
        return (
            <SessionFailed
                refusal={sessionRefusal(error)}
                onRetry={() => {
                    setMissingToken(false);
                    setAttempt((current) => current + 1);
                }}
            />
        );
    }

    return <Loading />;
};

/**
 * What a refused session start means for the person, read from the answer.
 *
 * A 409: this sign-in's address already belongs to another account (the Api
 * keeps one account per address). Most likely a Google profile that was never
 * linked to the password account, so trying again cannot help; signing in the
 * way the person did before can.
 *
 * A 403 is single sign-on having a say (AuthSessionController): either the
 * organization's identity provider asserted an address that reaches no
 * account here, or the person signed in some other way than through the
 * single sign-on their organization uses -- in which case the answer names
 * the provider, and the screen can send them straight to it.
 */
type SessionRefusal =
    | { kind: "email_in_use" }
    | { kind: "sso_sign_in_refused"; message: string }
    | { kind: "sso_required"; message: string; providerName: string };

function sessionRefusal(error: unknown): SessionRefusal | null {
    if (!isApiError(error)) {
        return null;
    }
    const { status, data, code, message = "" } = error;
    if (status === 409) {
        return { kind: "email_in_use" };
    }
    if (status !== 403) {
        return null;
    }
    if (code === "sso_sign_in_refused") {
        return { kind: "sso_sign_in_refused", message };
    }
    // The provider is session-specific; the original error body is not camelized.
    if (
        code === "sso_required" &&
        typeof data === "object" &&
        data !== null &&
        "provider_name" in data &&
        typeof data.provider_name === "string"
    ) {
        return {
            kind: "sso_required",
            message,
            providerName: data.provider_name,
        };
    }
    return null;
}

function refusalCopy(refusal: SessionRefusal | null): {
    title: string;
    body: string;
} {
    if (refusal === null) {
        return {
            title: "We couldn't open your account",
            body: "Try again. If it keeps happening, sign out and sign back in.",
        };
    }
    switch (refusal.kind) {
        case "email_in_use":
            return {
                title: "This email address has another account",
                body: "Sign out, then sign in the way you did before. If you can't, email support@spectaclehq.com.",
            };
        case "sso_sign_in_refused":
            return {
                title: "Single sign-on can't sign you in",
                body: refusal.message,
            };
        case "sso_required":
            return {
                title: "Your organization uses single sign-on",
                body: refusal.message,
            };
    }
}

const SessionFailed = ({
    refusal,
    onRetry,
}: {
    refusal: SessionRefusal | null;
    onRetry: () => void;
}) => {
    const { signOut } = useSignOut();
    const [leaving, setLeaving] = useState(false);
    const { title, body } = refusalCopy(refusal);

    /**
     * Off to the organization's identity provider. Amplify refuses to start
     * a redirect while it can read a signed-in user from storage, and the
     * session this sign-in made is not the one wanted, so its tokens are
     * swept first. Swept, not signed out: after a sign-in through a redirect
     * (Google, which is how two of the three cases this screen exists for
     * arrive) Amplify's own sign-out is itself a navigation, to the pool
     * domain's logout endpoint, and would race the redirect below. The
     * refresh token left unrevoked belongs to a profile that reaches no
     * account.
     */
    const continueWithSso = async (providerName: string) => {
        setLeaving(true);
        try {
            clearCognitoLocalStorage();
            await continueWithSingleSignOn(providerName);
        } catch (err) {
            console.error("Could not continue with single sign-on:", err);
            setLeaving(false);
        }
    };

    // Signing out is the whole answer for the two refusals nothing else can
    // help with; beside another action it is the quiet option.
    const signOutIsTheAnswer =
        refusal?.kind === "email_in_use" ||
        refusal?.kind === "sso_sign_in_refused";

    return (
        <div className={"flex flex-col items-center justify-center h-full"}>
            <Panel className={"max-w-md"}>
                <div className="flex items-center justify-center mb-8">
                    <Logomark className="h-8" />
                </div>

                <div className="text-center">
                    <h2 className="text-xl font-semibold text-zinc-950 dark:text-white mb-2">
                        {title}
                    </h2>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                        {body}
                    </p>
                </div>

                <div className="mt-6 flex flex-col gap-3">
                    {refusal?.kind === "sso_required" && (
                        <Button
                            type="primary"
                            onClick={() =>
                                continueWithSso(refusal.providerName)
                            }
                            loading={leaving}
                            className="w-full"
                        >
                            Use single sign-on
                        </Button>
                    )}
                    {refusal === null && (
                        <Button
                            type="primary"
                            onClick={onRetry}
                            className="w-full"
                        >
                            Try again
                        </Button>
                    )}
                    {signOutIsTheAnswer ? (
                        <Button
                            type="primary"
                            onClick={signOut}
                            className="w-full"
                        >
                            Sign out
                        </Button>
                    ) : (
                        <Button plain onClick={signOut} className="w-full">
                            Sign out
                        </Button>
                    )}
                </div>
            </Panel>
        </div>
    );
};

const VerificationCodeSchema = yup.object().shape({
    code: yup
        .string()
        .required("Verification code is required")
        .length(6, "Code must be 6 digits"),
});

const EmailCodeVerification = () => {
    const [, setNeedsVerification] = useUserVerification();
    const [error, setError] = useState<string | undefined>(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [lastResendTime, setLastResendTime] = useState<number | null>(null);
    const [canResend, setCanResend] = useState(true);
    const [secondsRemaining, setSecondsRemaining] = useState(0);
    const { signOut } = useSignOut();

    useEffect(() => {
        if (lastResendTime === null) {
            return;
        }

        const updateCountdown = () => {
            const now = Date.now();
            const timeSinceLastResend = now - lastResendTime;
            const remaining = Math.max(0, 60000 - timeSinceLastResend);

            if (remaining === 0) {
                setCanResend(true);
                setSecondsRemaining(0);
            } else {
                setSecondsRemaining(Math.ceil(remaining / 1000));
            }
        };

        const interval = setInterval(updateCountdown, 1000);
        updateCountdown();

        return () => clearInterval(interval);
    }, [lastResendTime]);

    const handleResendCode = async () => {
        if (!canResend) {
            return;
        }

        try {
            await sendUserAttributeVerificationCode({
                userAttributeKey: "email",
            });
            setLastResendTime(Date.now());
            setCanResend(false);
            setError(undefined);
        } catch (err) {
            console.error("Failed to resend code:", err);
            setError("Failed to resend code. Please try again.");
        }
    };

    const verifyEmail = async (code: string) => {
        try {
            await confirmUserAttribute({
                userAttributeKey: "email",
                confirmationCode: code,
            });

            // The cached ID token still carries the old address, and the
            // session started next is what copies the address onto the
            // account. Without a new token it would copy the old one back.
            await fetchAuthSession({ forceRefresh: true }).catch(
                () => undefined,
            );

            setNeedsVerification(false);
            return true;
        } catch (err) {
            console.error("Verification failed:", err);
            return false;
        }
    };

    return (
        <div className={"flex flex-col items-center justify-center h-full"}>
            <Panel className={"max-w-md"}>
                <div className="flex items-center justify-center mb-8">
                    <Logomark className="h-8" />
                </div>

                <div className="text-center mb-8">
                    <h2 className="text-xl font-semibold text-zinc-950 dark:text-white mb-2">
                        Verify your email
                    </h2>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                        We&apos;ve sent a verification code to your email
                        address. Please enter it below.
                    </p>
                </div>

                <Form
                    initialValues={{ code: "" }}
                    validationSchema={VerificationCodeSchema}
                    onSubmit={async (values, { setSubmitting }) => {
                        setIsSubmitting(true);
                        setError(undefined);

                        const success = await verifyEmail(values.code);

                        if (!success) {
                            setError(
                                "Invalid verification code. Please check and try again.",
                            );
                        }

                        setIsSubmitting(false);
                        setSubmitting(false);
                    }}
                >
                    {({ errors, touched }) => (
                        <>
                            <FormStack>
                                <Form.Field
                                    name="code"
                                    label="Verification code"
                                    placeholder="Enter 6-digit code"
                                    error={errors.code as string}
                                    touched={touched.code as boolean}
                                    autoFocus={true}
                                />
                            </FormStack>
                            {error && (
                                <div className="mt-3">
                                    <FormError>{error}</FormError>
                                </div>
                            )}

                            <div className="mt-6 flex flex-col gap-3">
                                <Button
                                    nativeType="submit"
                                    type="primary"
                                    loading={isSubmitting}
                                    className="w-full"
                                >
                                    Verify email
                                </Button>

                                <Button
                                    plain
                                    onClick={handleResendCode}
                                    disabled={!canResend}
                                    className="w-full"
                                >
                                    {canResend
                                        ? "Resend code"
                                        : `Resend code (${secondsRemaining}s)`}
                                </Button>
                            </div>

                            <div className={"mt-8 text-right text-xs"}>
                                <Link className="subtle" onClick={signOut}>
                                    Sign out
                                </Link>
                            </div>
                        </>
                    )}
                </Form>
            </Panel>
        </div>
    );
};
